import { useRef, useState } from 'react'
import { updateFolderSync, getUserInfo } from '../../../modules/TodoFolderSync'
import { createTask } from '../../../modules/TodoManager'
import { TodoFolder } from '../../../modules/TodoFolder'
import { FilterType } from '../../../modules/TodoFilter'
import { TodoItem } from '../../../modules/TodoItem'

interface TaskAlarm {
  action: string
  trigger: string | Date
  description: string
}

interface TaskData {
  summary: string
  dueDate?: Date | null
  recurrenceRule?: string | null
  reminderTime?: string | Date | null
  alarms?: TaskAlarm[]
  priority?: 'LOW' | 'MEDIUM' | 'HIGH'
  folder: TodoFolder
}

interface QuickAddTaskEditorHandle {
  clearAll: () => void
}

interface UseQuickAddReturn {
  showQuickAddTask: boolean
  quickAddTaskRef: React.RefObject<QuickAddTaskEditorHandle | null>
  onClickQuickAddTask: () => void
  handleQuickAddTask: (taskData: TaskData | string) => Promise<void>
  handleQuickAddCancel: () => void
  hideQuickAddTask: () => void
}

export const useQuickAdd = (
  currentFilter: FilterType,
  onTodoSelect: (todo: TodoItem | null) => void,
  loadTodos: () => void,
  _folders: TodoFolder[] = [],
): UseQuickAddReturn => {
  const [showQuickAddTask, setShowQuickAddTask] = useState<boolean>(false)
  const quickAddTaskRef = useRef<QuickAddTaskEditorHandle | null>(null)

  const onClickQuickAddTask = (): void => {
    setShowQuickAddTask(true)
    // selectedTodo 초기화
    onTodoSelect(null)
  }

  const handleQuickAddTask = async (taskData: TaskData | string): Promise<void> => {
    console.log('Adding new task:', taskData)

    const summary = typeof taskData === 'string' ? taskData : taskData.summary
    const dueDate = typeof taskData === 'object' ? taskData.dueDate : null
    const recurrenceRule = typeof taskData === 'object' ? taskData.recurrenceRule || null : null
    const reminderTime = typeof taskData === 'object' ? taskData.reminderTime : null
    let alarms = typeof taskData === 'object' ? taskData.alarms || [] : []
    const priority = typeof taskData === 'object' ? taskData.priority || 'MEDIUM' : 'MEDIUM'

    // Convert reminderTime to alarms format if provided
    if (reminderTime && !alarms.length) {
      alarms = [
        {
          action: 'DISPLAY',
          trigger: reminderTime,
          description: summary || 'Task Reminder',
        },
      ]
    }

    const targetFolder = (taskData as TaskData).folder
    if (targetFolder.isSync === false) {
      updateFolderSync(targetFolder.url, true)
    }

    const userInfo = getUserInfo()
    // const targetFolder = todoFolders.length > 0 ? todoFolders[0] : folders[0]
    // 서버에 추가 (즉시 sync 위해 임시 UI 업데이트 없이 진행)
    createTask(
      {
        url: null as any,
        uid: crypto.randomUUID(),
        folderUrl: targetFolder.url,
        etag: '',
        summary: summary,
        description: null,
        status: 'NEEDS-ACTION',
        priority: priority,
        percentComplete: 0,
        startDate: dueDate || null,
        dueDate: dueDate || null,
        dtstamp: new Date(),
        location: null,
        createdDate: new Date().toISOString() as any,
        recurrenceRule: recurrenceRule as any,
        datatype: null,
        alarms: alarms as any,
        author: {
          val: userInfo.email || '',
          params: {
            CN: userInfo.name || '',
          },
        },
        unknownProperties: [],
        datetype: 'date',
        lastModifiedDate: null,
      },
      targetFolder,
      (success: boolean) => {
        // Option B 패턴: createTask에서 optimistic update 처리
        // 성공/실패 관계없이 추가 UI 업데이트 불필요
        console.log('Quick add task result:', success ? 'success' : 'failed')
      },
    )
  }

  const handleQuickAddCancel = (): void => {
    hideQuickAddTask()
  }

  const hideQuickAddTask = (): void => {
    setShowQuickAddTask(false)
  }

  return {
    showQuickAddTask,
    quickAddTaskRef,
    onClickQuickAddTask,
    handleQuickAddTask,
    handleQuickAddCancel,
    hideQuickAddTask,
  }
}
